import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Users, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import UserAvatar from '../Common/UserAvatar';

const ProfileFriendsModal = ({ isOpen, onClose, friends = [], loading = false }) => {
  const { t } = useTranslation('profile');

  const getDisplayName = (friend) => {
    if (friend?.nickname?.trim()) return friend.nickname.trim();
    if (friend?.name?.trim()) return friend.name.trim();
    if (friend?.email) return friend.email.split('@')[0];
    return t('friendsModal.unknownName');
  };

  const activeFriends = useMemo(
    () => friends.filter((f) => !f.isDeleted),
    [friends],
  );

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[100000] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={onClose}
          />

          <motion.div
            className="relative w-full max-w-md bg-[#0d1424] border border-white/[0.08] rounded-2xl shadow-[0_32px_80px_rgba(0,0,0,0.7)] overflow-hidden"
            initial={{ opacity: 0, scale: 0.95, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 12 }}
            transition={{ duration: 0.18 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-[#3d8ef0]/15 flex items-center justify-center">
                  <Users className="w-4 h-4 text-[#3d8ef0]" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-white">{t('friendsModal.title')}</h3>
                  <p className="text-xs text-[#4d6a8a]">{t('friendsModal.friendsCount', { count: activeFriends.length })}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 rounded-lg text-[#4d6a8a] hover:text-[#c8d8eb] hover:bg-white/[0.06] transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="max-h-[min(60vh,420px)] overflow-y-auto p-3">
              {loading ? (
                <div className="flex items-center justify-center gap-2 py-12 text-[#4d6a8a] text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{t('friendsModal.loading')}</span>
                </div>
              ) : activeFriends.length === 0 ? (
                <div className="text-center py-12 px-4">
                  <Users className="w-10 h-10 mx-auto mb-3 text-[#4d6a8a] opacity-40" />
                  <p className="text-sm text-[#7a96b0]">{t('friendsModal.emptyTitle')}</p>
                  <p className="text-xs text-[#4d6a8a] mt-1">{t('friendsModal.emptyDescription')}</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {activeFriends.map((friend) => {
                    const displayName = getDisplayName(friend);
                    const handle = friend.email ? `@${friend.email.split('@')[0]}` : '';
                    const friendKey = friend.email || friend.user_id || friend.id;

                    return (
                      <div
                        key={friendKey}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/[0.04] transition-colors"
                      >
                        <UserAvatar
                          userId={friend.user_id || friend.id}
                          avatarUrl={friend.avatarUrl || friend.avatar_url}
                          name={displayName}
                          size="md"
                          online={!!friend.is_online}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#e4edf7] truncate">{displayName}</p>
                          {handle && (
                            <p className="text-xs text-[#4d6a8a] truncate">{handle}</p>
                          )}
                        </div>
                        {friend.is_online && (
                          <span className="text-[10px] font-medium text-emerald-400 shrink-0">{t('friendsModal.online')}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default ProfileFriendsModal;
